import apiClient from './client';

// Current Terms of Service version - update when ToS changes.
// The combined Cloud UniFi / NetConsole terms published 2026-09-15 are served
// at both help.cloudunifi.com/terms/ and netconsole.cloud/terms/.
export const TOS_VERSION = 'cloudunifi-tos-2026-09-15';

interface MigrationAcceptanceParams {
  subscriptionId: string;
  tosVersion: string;
  ownerAdminId: string;
  acknowledgments: {
    tos_acceptance: boolean;
    end_user_infrastructure_acknowledgment: boolean;
  };
}

interface MigrationResponse {
  success: boolean;
  uos_hostname?: string;
  uos_url?: string;
  message?: string;
}

interface MigrationEligibility {
  eligible: boolean;
  reason?: string;
}

export interface AdminEnumerationAdmin {
  admin_id: string;
  name: string;
  email: string;
  ubic_uuid: string;
  has_cloud_sso: boolean;
  is_super_admin: boolean;
  site_privileges: Array<{
    site_id: string;
    site_name: string;
    role: string;
  }>;
}

export interface AdminEnumerationResult {
  admins: AdminEnumerationAdmin[];
  cloud_access: {
    enabled: boolean;
    cloud_owner_ubic_uuid: string;
  };
}

interface EnumerationResponse {
  success: boolean;
  status?: 'pending' | 'running' | 'completed';
  data?: AdminEnumerationResult | null;
  error?: string;
  message?: string;
}

export interface MigrationStatus {
  has_migration: boolean;
  status?: 'pending' | 'exporting' | 'provisioning' | 'informing' | 'active' | 'failed';
  uos_hostname?: string;
  error_message?: string | null;
  created_at?: string;
  updated_at?: string;
  export_completed_at?: string | null;
  provisioned_at?: string | null;
  completed_at?: string | null;
  // Custom domain migration fields
  source_custom_hostname?: string | null;
  custom_domain_migration_status?: 'pending' | 'dns_verified' | 'provisioning' | 'active' | 'failed' | null;
  custom_domain_dns_verified_at?: string | null;
  custom_domain_provisioned_at?: string | null;
  custom_domain_error?: string | null;
  // Owner login info
  owner_username?: string | null;
  owner_email?: string | null;
  owner_cloud_linked?: boolean | null;
  // Dismiss tracking
  migration_dismissed_at?: string | null;
  // Source controller state (account-level Remote Access toggle on the source NWA — NOT the chosen owner's cloud-link state; use owner_cloud_linked for that)
  source_cloud_access_enabled?: boolean;
  // Live Remote Access state on the NEW UOS console (unauthenticated /api/system probe).
  // Only populated while the post-migration Remote Access step is relevant
  // (active, not dismissed, source had it on). null = not checked / could not determine.
  uos_remote_access_enabled?: boolean | null;
}

export async function acceptUosMigration(
  params: MigrationAcceptanceParams
): Promise<MigrationResponse> {
  const response = await apiClient.post<MigrationResponse>(
    `/api/v1/subscriptions/${params.subscriptionId}/migrate-uos`,
    {
      tos_version: params.tosVersion,
      owner_admin_id: params.ownerAdminId,
      acknowledgments: params.acknowledgments,
    }
  );
  return response.data;
}

export async function enumerateAdmins(
  subscriptionId: string
): Promise<{ action_id: string }> {
  const response = await apiClient.post<{ success: boolean; action_id: string }>(
    `/api/v1/subscriptions/${subscriptionId}/enumerate-admins`
  );
  return response.data;
}

export async function getAdminEnumeration(
  subscriptionId: string
): Promise<EnumerationResponse> {
  const response = await apiClient.get<EnumerationResponse>(
    `/api/v1/subscriptions/${subscriptionId}/admin-enumeration`
  );
  return response.data;
}

export async function checkMigrationEligibility(
  subscriptionId: string
): Promise<MigrationEligibility> {
  const response = await apiClient.get<MigrationEligibility>(
    `/api/v1/subscriptions/${subscriptionId}/migration-eligibility`
  );
  return response.data;
}

export async function getMigrationStatus(
  subscriptionId: string
): Promise<MigrationStatus> {
  const response = await apiClient.get<{ success: boolean; data: MigrationStatus }>(
    `/api/v1/subscriptions/${subscriptionId}/migration-status`
  );
  return response.data.data;
}

export async function dismissMigration(subscriptionId: string): Promise<void> {
  await apiClient.post(`/api/v1/subscriptions/${subscriptionId}/migration/dismiss`);
}

export async function requestUosMigration(
  subscriptionId: string
): Promise<{ success: boolean; message?: string }> {
  const response = await apiClient.post<{ success: boolean; message?: string }>(
    `/api/v1/subscriptions/${subscriptionId}/request-uos-migration`
  );
  return response.data;
}
